import { Avatar, Badge, Button, Dialog, Flex, Heading, IconButton, Separator, Text } from '@radix-ui/themes';
import React, { useState } from 'react';
import { CgArrowsExpandUpRight } from 'react-icons/cg';
import { MdOutlineVerifiedUser } from 'react-icons/md';
import { RiDeleteBin5Fill } from 'react-icons/ri';
import { createPortal } from 'react-dom';
import ConfirmationDialogue from './ConfirmationDialogue';
import axiosInstance from '../../config/axiosConfig';
import { FaHospital } from "react-icons/fa";
import DialogCloseButton from './DialogCloseButton';

const ViewInstitution = ({ initialValues = null, onInstitutionUpdated, showToast }) => {
  const [isViewOpen, setViewOpen] = useState(false);
  const [confirmationOpen, setConfirmationOpen] = useState(false);
  const [confirmationType, setConfirmationType] = useState(null);

  const handleConfirmation = (type) => {
    setViewOpen(false);
    setConfirmationType(type);
    setConfirmationOpen(true);
  };

  const confirmationHeadings = {
    Delete: 'Deletion',
    Activate: 'Activation',
    Deactivate: 'Deactivation',
  };

  const mapRoomsToLicenceType = (rooms) => {
    if (rooms <= 50) return '0-50';
    if (rooms <= 100) return '0-100';
    return '0-200';
  };

  const handleDelete = async () => {
    try {
      const response = await axiosInstance.post(`/institution/delete/${initialValues.token}`);
      if (response.data.result) {
        if (onInstitutionUpdated) {
          await onInstitutionUpdated();
        }
        return true;
      } else {
        console.error('Delete failed:', response.data);
        return false;
      }
    } catch (error) {
      console.error('Error deleting institution:', error);
      return false;
    }
  };

  const handleUpdateStatus = async (isActive) => {
    try {
      // Updated institutionData with only token and isActive, others null
      const institutionData = {
        institutionModel: {
          token: initialValues.token, // Required identifier
          isActive: isActive, // Status to update
          name: null, 
          type: null,
          address: null,
          email: null,
          countryCode: null,
          phoneNo: null,
          rooms: null,
          userName: null,
          firstName: null,
          lastName: null,
          isDeleted: null,
          isEmailConfirmed: null,
        },
      };

      const response = await axiosInstance.post('/institution/update', institutionData);
      if (response.data.result) {
        if (onInstitutionUpdated) {
          await onInstitutionUpdated();
        }
        return true;
      } else {
        console.error('Update failed:', response.data);
        return false;
      }
    } catch (error) {
      console.error('Error updating institution:', error);
      return false;
    }
  };

  const handleConfirmAction = async () => {
    let success = false;
    let actionMessage = '';
    if (confirmationType === 'Delete') {
      success = await handleDelete();
      actionMessage = 'Deleted';
    } else if (confirmationType === 'Activate') {
      success = await handleUpdateStatus(true);
      actionMessage = 'Activated';
    } else if (confirmationType === 'Deactivate') {
      success = await handleUpdateStatus(false);
      actionMessage = 'Deactivated';
    }

    if (success) {
      const message = `${actionMessage} successfully`;
      if (showToast) {
        showToast(message);
      }
      setConfirmationOpen(false);
    }
  };

  const dialogContent = isViewOpen ? (
    <>
      <Dialog.Content maxWidth="450px">
        <DialogCloseButton onClose={() => setViewOpen(false)} />
        <Flex direction="column" gap="3">
          <Flex gap="2" align="center">
            <Avatar fallback="I" />
            <Flex direction="column">
              <Heading size="4">{initialValues.name}</Heading>
              <Flex direction="row" gap="2">
                <Text size="1">{initialValues.email}</Text>
                <Separator orientation="vertical" />
                <Badge color={initialValues.isActive ? 'green' : 'tomato'}>
                  <MdOutlineVerifiedUser color={initialValues.isActive ? 'green' : 'red'} />
                  {initialValues.isActive ? 'Active' : 'Inactive'}
                </Badge>
              </Flex>
            </Flex>
          </Flex>

          <Flex direction="row" gap="4" align="center">
            <Flex direction="column" className="w-[30%]">
              <Heading size="1">Phone Number</Heading>
              <Text size="1">{initialValues.phoneNo}</Text>
            </Flex>
            <Separator orientation="vertical" />
            <Flex direction="column" className="w-[30%]">
              <Heading size="1">Institution ID</Heading>
              <Text size="1">{initialValues.institutionNumber}</Text>
            </Flex>
            <Separator orientation="vertical" />
            <Flex direction="column" className="w-[30%]">
              <Heading size="1">Username</Heading>
              <Text size="1">{initialValues.userName}</Text>
            </Flex>
          </Flex>

          <Flex direction="row" gap="4" align="center">
            <Flex direction="column" className="w-[30%]">
              <Heading size="1">Address</Heading>
              <Text size="1">{initialValues.address}</Text>
            </Flex>
            <Separator orientation="vertical" />
            <Flex direction="column" className="w-[30%]">
              <Heading size="1">Institution Type</Heading>
              <Text size="1">{initialValues.type || 'Hospital'}</Text>
            </Flex>
            <Separator orientation="vertical" />
            <Flex direction="column" className="w-[30%]">
              <Heading size="1">Licence Type</Heading>
              <Text size="1">{mapRoomsToLicenceType(initialValues.rooms)}</Text>
            </Flex>
          </Flex>

          <Flex direction="row" gap="1" align="center" justify="end">
            <Button
              color={initialValues.isActive ? 'crimson' : 'green'}
              onClick={() => handleConfirmation(initialValues.isActive ? 'Deactivate' : 'Activate')}
            >
              {initialValues.isActive ? 'Deactivate' : 'Activate'}
            </Button>
            <Button onClick={() => handleConfirmation('Delete')}>
              <RiDeleteBin5Fill /> Delete
            </Button>
          </Flex>
        </Flex>
      </Dialog.Content>
    </>
  ) : null;

  return (
    <>
      <Dialog.Root open={isViewOpen} onOpenChange={setViewOpen}>
        <Dialog.Trigger>
          <IconButton variant="solid" onClick={() => setViewOpen(true)}>
            <CgArrowsExpandUpRight width="18" height="18" />
          </IconButton>
        </Dialog.Trigger>
        {isViewOpen && createPortal(dialogContent, document.body)}
      </Dialog.Root>

      {confirmationOpen && (
        <ConfirmationDialogue
          Name={initialValues.name}
          Type={confirmationHeadings[confirmationType] || 'Confirm Action'}
          Action={confirmationType.toLowerCase()}
          onCancel={() => setConfirmationOpen(false)}
          onConfirm={handleConfirmAction}
        />
      )}
    </>
  );
};

export default ViewInstitution;